import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ArrowRight,
  Battery,
  Bike,
  ExternalLink,
  FoldHorizontal,
  Gauge,
  Shield,
  Weight,
  type LucideIcon
} from 'lucide-react';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { PageHero } from '../components/shared/PageHero';
import { Button } from '../components/ui/button';
import { electricBikes, type ElectricBike } from '../data/electricBikes';
import { useSEO } from '../hooks/useSEO';

const ELECTRIC_BIKES_CATEGORY_URL = 'https://zwheel-shop.com/product-category/bicicletas-electricas/';

const metricIcons: Record<string, LucideIcon> = {
  'Autonomía': Battery,
  Potencia: Gauge,
  Peso: Weight,
  Velocidad: Bike
};

function BikeOverviewCard({ bike }: { bike: ElectricBike }) {
  return (
    <AnimatedSection className="h-full">
      <div className={`h-full rounded-[32px] border ${bike.cardTheme.border} bg-white/90 p-6 shadow-[0_20px_60px_rgba(17,17,17,0.08)] backdrop-blur`}>
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] ${bike.cardTheme.surface} ${bike.cardTheme.accent}`}>
              {bike.badge}
            </span>
            <h2 className="mt-4 text-2xl md:text-3xl font-bold text-gray-900">{bike.fullName}</h2>
            {bike.price && (
              <p className={`mt-3 text-2xl font-bold ${bike.cardTheme.accent}`}>
                {bike.price}
              </p>
            )}
            <p className="mt-3 text-gray-600 leading-relaxed">{bike.summary}</p>
          </div>
          <img
            src={bike.heroImage}
            alt={bike.fullName}
            className="hidden w-36 flex-none rounded-[28px] border border-white bg-white p-3 shadow-md lg:block"
          />
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6">
          {bike.metrics.map((metric) => {
            const Icon = metricIcons[metric.label] || Bike;
            return (
              <div key={metric.label} className={`rounded-2xl border ${bike.cardTheme.border} ${bike.cardTheme.surface} p-4`}>
                <Icon className={`h-5 w-5 ${bike.cardTheme.accent}`} />
                <div className="mt-3 text-xl font-bold text-gray-900">{metric.value}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.14em] text-gray-500">{metric.label}</div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="text-sm font-medium uppercase tracking-[0.16em] text-gray-500">
            Disponible en la tienda oficial
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline">
              <a href={`#${bike.id}`}>
                Descubrir modelo
                <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
            <Button asChild className="bg-black text-white hover:bg-gray-900">
              <a href={bike.shopUrl} target="_blank" rel="noopener noreferrer">
                Comprar ahora
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
}

function BikeDetailSection({ bike, reverse = false }: { bike: ElectricBike; reverse?: boolean }) {
  const [selectedImage, setSelectedImage] = useState(0);

  return (
    <section
      id={bike.id}
      className={`relative overflow-hidden scroll-mt-24 py-20 md:py-24 ${
        reverse ? 'bg-[linear-gradient(180deg,#ffffff_0%,#f8fbf8_100%)]' : 'bg-[linear-gradient(180deg,#fbf7f0_0%,#ffffff_100%)]'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className={`grid grid-cols-1 items-start gap-10 lg:grid-cols-[1fr_1.02fr] ${reverse ? 'lg:[&>*:first-child]:order-2' : ''}`}>
          <AnimatedSection direction={reverse ? 'left' : 'right'}>
            <div className="space-y-4">
              <div className={`rounded-[32px] border ${bike.cardTheme.border} bg-white p-4 shadow-[0_20px_60px_rgba(17,17,17,0.08)]`}>
                <div className={`rounded-[28px] bg-gradient-to-br ${bike.cardTheme.tint} p-4 sm:p-6`}>
                  <img
                    src={bike.gallery[selectedImage]}
                    alt={`${bike.fullName} ${selectedImage + 1}`}
                    className="h-full w-full rounded-[24px] object-contain"
                  />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-3 sm:grid-cols-6">
                {bike.gallery.map((image, index) => (
                  <button
                    key={image}
                    type="button"
                    onClick={() => setSelectedImage(index)}
                    className={`overflow-hidden rounded-2xl border bg-white transition-all ${
                      selectedImage === index
                        ? `scale-[1.02] ${bike.cardTheme.border} shadow-md`
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${bike.name} miniatura ${index + 1}`}
                      className="aspect-square w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.08}>
            <div className="rounded-[32px] border border-gray-200 bg-white p-6 shadow-[0_20px_60px_rgba(17,17,17,0.06)] sm:p-8">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] ${bike.cardTheme.surface} ${bike.cardTheme.accent}`}>
                    {bike.badge}
                  </span>
                  <h2 className="mt-4 text-3xl md:text-4xl font-bold text-gray-900">{bike.fullName}</h2>
                  {bike.price && (
                    <p className={`mt-3 text-3xl font-bold ${bike.cardTheme.accent}`}>
                      {bike.price}
                    </p>
                  )}
                  <p className="mt-3 text-lg text-gray-600">{bike.tagline}</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button asChild className="bg-black text-white hover:bg-gray-900">
                    <a href={bike.shopUrl} target="_blank" rel="noopener noreferrer">
                      Comprar {bike.name}
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                  <Button asChild variant="outline">
                    <a href={ELECTRIC_BIKES_CATEGORY_URL} target="_blank" rel="noopener noreferrer">
                      Ver categoría
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4">
                {bike.metrics.map((metric) => {
                  const Icon = metricIcons[metric.label] || Bike;
                  return (
                    <div key={metric.label} className={`rounded-2xl border ${bike.cardTheme.border} ${bike.cardTheme.surface} p-4`}>
                      <Icon className={`h-5 w-5 ${bike.cardTheme.accent}`} />
                      <div className="mt-3 text-xl font-bold text-gray-900">{metric.value}</div>
                      <div className="mt-1 text-xs uppercase tracking-[0.14em] text-gray-500">{metric.label}</div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 space-y-4 text-gray-600">
                {bike.paragraphs.map((paragraph) => (
                  <p key={paragraph} className="leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
                <div className={`rounded-[28px] ${bike.cardTheme.surface} p-6`}>
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className={`h-5 w-5 ${bike.cardTheme.accent}`} />
                    <h3 className="text-lg font-semibold text-gray-900">Resumen del modelo</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm uppercase tracking-[0.16em] text-gray-500">{bike.focusLabel}</div>
                      <div className="mt-1 text-lg font-semibold text-gray-900">{bike.focusValue}</div>
                    </div>
                    <div>
                      <div className="text-sm uppercase tracking-[0.16em] text-gray-500">Escoge tu color</div>
                      <div className="mt-3 flex flex-wrap gap-3">
                        {bike.colors.map((color) => {
                          const content = (
                            <>
                              <span
                                className="h-4 w-4 rounded-full border border-black/10"
                                style={{ backgroundColor: color.swatch }}
                                aria-hidden="true"
                              />
                              <span className="text-sm text-gray-700">{color.name}</span>
                            </>
                          );

                          return color.url ? (
                            <a
                              key={color.name}
                              href={color.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-2 transition-colors hover:border-black/30"
                            >
                              {content}
                            </a>
                          ) : (
                            <div key={color.name} className="flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-2">
                              {content}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="pt-2">
                      <div className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-2 text-sm text-gray-700">
                        <FoldHorizontal className="h-4 w-4" />
                        {bike.specs.find((spec) => spec.label === 'Plegable')?.value === 'Sí'
                          ? 'Sistema plegable'
                          : 'Chasis no plegable'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  {bike.highlights.map((highlight) => (
                    <div key={highlight} className="rounded-2xl border border-gray-200 bg-white p-4">
                      <div className={`mb-3 h-2 w-10 rounded-full ${bike.cardTheme.accentSoft}`} />
                      <p className="text-sm leading-relaxed text-gray-700">{highlight}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>

        <AnimatedSection delay={0.12} className="mt-10">
          <div className="rounded-[32px] border border-gray-200 bg-white p-6 shadow-[0_20px_60px_rgba(17,17,17,0.05)] sm:p-8">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-gray-500">Ficha técnica</p>
                <h3 className="mt-2 text-2xl md:text-3xl font-bold text-gray-900">{bike.name} en detalle</h3>
              </div>
              <p className="max-w-2xl text-sm text-gray-500">
                Todos los datos clave del modelo en un formato rápido de consultar.
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              {bike.specs.map((spec) => (
                <div key={spec.label} className="flex items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
                  <span className="text-sm font-medium text-gray-500">{spec.label}</span>
                  <span className="text-right text-sm font-semibold text-gray-900">{spec.value}</span>
                </div>
              ))}
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

export default function ElectricBikes() {
  const { hash } = useLocation();

  useSEO({
    title: 'Bicicletas eléctricas Urban Pioneer, Urban Flux y Urban Realm',
    description:
      'Descubre las bicicletas eléctricas Zwheel Urban Pioneer, Urban Flux y Urban Realm: motor de 250W, hasta 130 km de autonomía, frenos hidráulicos y diseño urbano.',
    keywords:
      'bicicletas eléctricas, Urban Pioneer, Urban Flux, Urban Realm, Zwheel, bicicleta urbana eléctrica, bicicleta plegable eléctrica'
  });

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);

      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className="overflow-hidden bg-white">
      <PageHero
        tag="Nueva gama e-bike"
        title="Bicicletas eléctricas"
        subtitle="Urban Pioneer, Urban Flux y Urban Realm comparten motor de 250W, autonomía pensada para el día a día y una base robusta para moverte con el estilo y la comodidad de Zwheel."
      />

      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            {electricBikes.map((bike) => (
              <BikeOverviewCard key={bike.id} bike={bike} />
            ))}
          </div>
        </div>
      </section>

      {electricBikes.map((bike, index) => (
        <BikeDetailSection key={bike.id} bike={bike} reverse={index % 2 !== 0} />
      ))}

      <section className="bg-[#111111] py-20 text-white">
        <div className="container mx-auto px-4">
          <AnimatedSection>
            <div className="mx-auto max-w-4xl text-center">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-white/60">Siguiente paso</p>
              <h2 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl">
                Elige la Urban que encaja con tu forma de moverte.
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-white/70">
                Si priorizas confort diario, Urban Pioneer es la opción natural. Si necesitas plegado y un formato más compacto, Urban Flux cubre mejor ese uso. Si buscas ruedas de carretera de 26" y hasta 130 km de autonomía, Urban Realm amplía la gama para trayectos más largos.
              </p>
              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
                <Button asChild size="lg" className="bg-white text-black hover:bg-white/90">
                  <a href={ELECTRIC_BIKES_CATEGORY_URL} target="_blank" rel="noopener noreferrer">
                    Ver toda la categoría
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
                {electricBikes.map((bike) => (
                  <Button key={bike.id} asChild size="lg" variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">
                    <a href={bike.shopUrl} target="_blank" rel="noopener noreferrer">
                      Comprar {bike.name}
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
